import { ShareApplicationWorkflowDialog } from "@/components/ShareApplicationWorkflow/ShareApplicationWorkflowDialog";
import { faShareNodes, faPaperPlane, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppToasterV2, PrimaryButton, SecondaryButton } from "@ucl/library";
import { dialogStore } from "@ucl/library/src/components/Dialogs/Dialog/stores/DialogStore";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import { ApplicationStatus } from "@/types/enums/ApplicationStatus";
import "./BankerApplicationViewPageHeader.scss";
import { getFirstLastName } from "@/components/ComplexFields/PersonInformationComplexField";
import { financialApplicationAtomFamily } from "@/atoms/financialApplicationAtomFamily";
import { useAtomValue } from "jotai";
import React from "react";
import { useNavigate } from "react-router-dom";
import { RemainingFields } from "@/components/RemainingFields/RemainingFields";
import { createBoltTaskApiClient } from "@bolt/task/src/lib/apiClients/boltTaskApiClient";
import { EntityTypes } from "@/types/EntityTypeMapping";
import { useBoltApiClient } from "@bolt/core";
import moment from "moment";
import { BankerValidationErrorsBadge } from "@/components/BankerApplicationDetails/BankerValidationErrorsBadge";
import { FinancialApplicationSection } from "@/types/enums";
import { isNil } from "lodash";
import { ValidationErrorDisplayMode } from "@/components/ValidationErrorsCounts/ValidationErrorsCounts";

export interface BankerApplicationViewPageHeaderPropsV2 {
  financialApplicationId: string;
  onCoreDataRefresh?: () => Promise<void>;
}

export const BankerApplicationViewPageHeaderV2: React.FC<BankerApplicationViewPageHeaderPropsV2> = (
  props
) => {
  const navigate = useNavigate();
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const boltApiClient = useBoltApiClient();
  const boltTaskApiClient = createBoltTaskApiClient(boltApiClient);

  const handleShareApplication = () => {
    dialogStore.openDialog({
      id: "share-application-dialog",
      header: "Share Application",
      allowDragging: false,
      width: "900px",
      content: () => (
        <ShareApplicationWorkflowDialog 
          financialApplicationId={props.financialApplicationId} 
          onCoreDataRefresh={props.onCoreDataRefresh}
        />
      ),
      close: () => {
        dialogStore.closeDialog("share-application-dialog");
      },
    });
  };

  const handleSubmitApplication = async () => {
    try {
      await financialApplicationApiClient.submitFinancialApplication(props.financialApplicationId);
      AppToasterV2.showSuccessAppToaster("Application submitted successfully");
    } catch (error: any) {
      AppToasterV2.showFailedActionToaster(
        error?.response?.data?.detail ?? "",
        "Failed to submit application"
      );
    }
  };

  const handleSubmitApplicationClick = () => {
    dialogStore.openConfirmDialog({
      dialogId: "submit-application-confirmation",
      headerText: "Submit Application",
      content: <p>Are you sure you want to submit this application?</p>,
      confirmButtonText: "Submit Application",
      onConfirm: async () => {
        const result = {
          isSuccess: true,
          error: undefined as any,
        };

        await handleSubmitApplication();

        return result;
      },
    });
  };

  const handleBackToApplications = () => {
    navigate("/banker");
  };

  const financialApplicationFormState = useAtomValue(
    financialApplicationAtomFamily(props.financialApplicationId)
  );
  const financialApplication = useAtomValue(financialApplicationFormState.financialApplication);

  const applicationData = financialApplication;
  const applicantFullName = applicationData
    ? getFirstLastName(applicationData.personInformation)
    : undefined;

  const outstandingTasks = applicationData?.outstandingTasksCount;
  const totalTasksCount = applicationData?.totalTasksCount;
  const applicationStatus = applicationData?.status;
  const isDraftStatus = applicationStatus === ApplicationStatus.Draft;

  return (
    <div className="banker-application-view__header">
      <div className="banker-application-view__breadcrumb">
        <SecondaryButton text="All Applications" 
          minimal={true}
          className="banker-application-view__back-link" 
          onClick={handleBackToApplications}
        />
        <span className="banker-application-view__breadcrumb-separator"><FontAwesomeIcon icon={faChevronRight} size="xs" /></span>
        <span className="banker-application-view__current-client">{applicantFullName}</span>
      </div>
      <div className="banker-application-view__stats">
        {!isNil(outstandingTasks) && (
          <>
          <BankerValidationErrorsBadge
            financialApplicationId={props.financialApplicationId}
            section={FinancialApplicationSection.AllSections}
            renderZeroErrors={true}
            label="Remaining Fields"
            showNavigateToField={true}
            showSectionTitle={true}
            popoverClassName="banker-application-view-page-header__popover"
            displayMode={ValidationErrorDisplayMode.RemainingFromTotal}
          />
          <RemainingFields
            remainingCount={outstandingTasks || 0}
            totalCount={totalTasksCount || 0}
            showFieldDetails={true}
            remainingFieldsPromiseFactory={async () => {
              const response = await boltTaskApiClient.getTasks(
                EntityTypes.FinancialApplication.EntityType,
                props.financialApplicationId,
                true
              );
              return response.map(
                (task) => `${task.taskTemplateName} (Due: ${moment(task.dueDate).format("M/D/YYYY")})`
              );
            }}
            label="Remaining Tasks"
              popoverLabel="Remaining Tasks"
            />
          </>
        )}
      </div>
      <div className="banker-application-view__action-buttons">
        {/* <PrimaryButton
          text={usesSticky ? "Use Tabbed Details" : "Use Scrollable Details"}
          onClick={() => {
            setUsesSticky(!usesSticky);
          }}
        /> */}
        {isDraftStatus && (
          <PrimaryButton
            text="Submit Application"
            onClick={handleSubmitApplicationClick}
            className="banker-application-view__submit-button"
            iconProps={{
              icon: faPaperPlane,
            }}
          />
        )}
        <PrimaryButton
          text="Share Application"
          onClick={handleShareApplication}
          className="banker-application-view__share-button"
          iconProps={{
            icon: faShareNodes,
          }}
        />
      </div>
    </div>
  );
};

export default BankerApplicationViewPageHeaderV2;
